import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Button, Card, Container, Flex, Grid, Image, List, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import ServiceTab from '@/components/landing/ServiceTab';
import { ROUTES } from '@/routes/paths';

interface ServiceSectionProps {
  readonly nsKey: 'trade' | 'certification';
  readonly cover: string;
  readonly featureKeys?: readonly string[];
  /** Кнопка регистрации в панели; призывов на странице немного, поэтому она есть не в каждом блоке. */
  readonly withCta?: boolean;
}

// Услуга: слева — таб-ярлык + заголовок + вступление; справа — композитный блок «фото + серая
// панель» (детали, буллеты, CTA) единым модулем. На мобильном всё стекуется, фото сверху композита.
export default function ServiceSection({ nsKey, cover, featureKeys, withCta }: ServiceSectionProps) {
  const { t } = useTranslation();
  const base = `landing.services.${nsKey}`;

  return (
    <Container id={nsKey} size="lg" py={{ base: 'xl', md: 48 }}>
      <Grid gap={{ base: 'lg', md: 'xl' }} align="flex-start">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="md">
            <ServiceTab>{t(`${base}.label`)}</ServiceTab>
            <Title order={2} fz={{ base: 22, sm: 30 }}>
              {t(`${base}.title`)}
            </Title>
            <Text fw={500}>{t(`${base}.intro`)}</Text>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          <Card withBorder radius={0} p={0} style={{ overflow: 'hidden' }}>
            <Flex direction={{ base: 'column', sm: 'row' }} align="stretch">
              <Image
                src={`/images/${cover}.webp`}
                alt=""
                fit="cover"
                w={{ base: '100%', sm: '42%' }}
                h={{ base: 200, sm: '100%' }}
              />
              <Box flex="1" bg="gray.0" p="lg" display="flex">
                {/* flex и mt="auto": текст в блоках разной длины, а кнопка всегда внизу панели. */}
                <Stack gap="md" flex={1}>
                  <Text c="gray.7" fz="sm">
                    {t(`${base}.detail`)}
                  </Text>
                  {featureKeys && (
                    <List
                      spacing="xs"
                      icon={
                        <ThemeIcon color="brand" size={20} radius="xl">
                          <IconCircleCheck size={13} />
                        </ThemeIcon>
                      }
                    >
                      {featureKeys.map((key) => (
                        <List.Item key={key}>
                          <Text fz="sm">{t(key)}</Text>
                        </List.Item>
                      ))}
                    </List>
                  )}
                  {withCta ? (
                    <Button component={Link} to={ROUTES.REGISTER} w="fit-content" mt="auto">
                      {t('landing.services.cta')}
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            </Flex>
          </Card>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
